import { useEffect, useState, useCallback } from 'react'
import { apiGet } from '../api'
import type { CalendarResponse } from '../api'
import CalendarPicker from './CalendarPicker'

const URG_TINT: Record<number, { fg: string; bg: string; lab: string }> = {
  5: { fg: '#ff6b81', bg: 'rgba(255,107,129,0.16)', lab: 'CRIT' },
  4: { fg: '#ff9e6d', bg: 'rgba(255,158,109,0.16)', lab: 'KEY' },
  3: { fg: '#7dd3fc', bg: 'rgba(125,211,252,0.14)', lab: 'STD' },
  2: { fg: '#9aa9bf', bg: 'rgba(154,169,191,0.10)', lab: 'OPT' },
  1: { fg: '#64748b', bg: 'rgba(100,116,139,0.10)', lab: 'BG' },
}

const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London' })
const day = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'Europe/London' })

export default function CalendarStrip() {
  const [data, setData] = useState<CalendarResponse | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await apiGet<CalendarResponse>('/calendar/next24')
      setData(res)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 10 * 60_000)
    return () => clearInterval(t)
  }, [load])

  return (
    <section className="panel work-panel" style={{ position: 'relative' }}>
      <div className="panel-header">
        <span>Next 24h</span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', position: 'relative' }}>
          {data && data.focus_block_minutes > 0 && (
            <span className="pill amber">{data.focus_block_minutes}m focus</span>
          )}
          <CalendarPicker />
        </span>
      </div>
      {!data && <div className="empty-state">Loading…</div>}
      {data && data.events.length === 0 && <div className="empty-state">Clear ahead.</div>}
      {data && data.events.length > 0 && (
        <div className="calendar-strip">
          {data.events.map(ev => {
            const start = new Date(ev.start_at)
            const u = ev.ai_urgency
            const tint = u && URG_TINT[u] ? URG_TINT[u] : null
            const prep = () => {
              const prompt = `Prep me for this meeting: "${ev.title}" at ${time.format(start)} on ${day.format(start)}${ev.location ? ` (${ev.location})` : ''}. Pull recent inbox context with the attendees if you can spot it, then list 3 talking points and 1 desired outcome. No fluff.`
              window.dispatchEvent(new CustomEvent('mercedes:open', { detail: { prompt } }))
            }
            return (
              <div key={ev.id} className={`calendar-event ${ev.is_focus_block ? 'focus' : ''}`}
                   title={ev.ai_reason ?? undefined}
                   style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 8px', alignItems: 'start' }}>
                <div className="when" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span className="muted">{day.format(start)}</span> {time.format(start)}
                  {tint && (
                    <span style={{
                      marginLeft: 4, padding: '1px 6px', borderRadius: 999,
                      fontSize: '0.6rem', letterSpacing: '0.08em',
                      background: tint.bg, color: tint.fg, border: `1px solid ${tint.fg}40`,
                    }}>{tint.lab}</span>
                  )}
                </div>
                <button type="button" onClick={prep} title="Prep talking points with Mercedes"
                  style={{ minHeight: 20, padding: '0 6px', fontSize: '0.6rem',
                           background: 'rgba(90,242,255,0.06)', borderColor: 'rgba(90,242,255,0.28)',
                           color: 'var(--accent)', alignSelf: 'start', justifySelf: 'end' }}>
                  📋 prep
                </button>
                <div className="title" style={{ gridColumn: '1 / -1' }}>
                  {ev.title}
                  {ev.location && <span className="muted"> · {ev.location}</span>}
                </div>
                {ev.ai_reason && (
                  <div className="muted" style={{ fontSize: '0.7rem', marginTop: 2, gridColumn: '1 / -1' }}>{ev.ai_reason}</div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
